import { Box, Collapse, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';

import { colors } from '../../../components/theme/theme';
import { useTranslation } from '../../../utils/translations';

export interface ScheduledPaymentMobileRow {
    contractId: React.ReactNode;
    dueDate: string;
    paymentMode: React.ReactNode;
    paymentModeCompact: React.ReactNode;
    amount: React.ReactNode;
    amountCompact: React.ReactNode;
    status: React.ReactNode;
    statusLabel: string;
    statusColor: string;
    comments: React.ReactNode;
    actions: React.ReactNode;
    hasCancelAction: boolean;
}

interface ScheduledPaymentMobileCardProps {
    row: ScheduledPaymentMobileRow;
}

const ChevronIcon: React.FC<{ expanded: boolean }> = ({ expanded }) => (
    <span
        className="icon-arrow-drop-down"
        style={{
            fontSize: 20,
            color: colors.greyPreset[100],
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
            display: 'inline-block',
        }}
    />
);

/**
 * Mobile/tablet-only card rendering of a single scheduled-payment row. Collapsed state
 * shows Contract+mode, Scheduled Date and Amount+status inline (matching the desktop
 * table's always-visible fields); expanding swaps the header for the status chip and
 * reveals Amount/Payment Mode/Comments/Cancel detail. Desktop keeps using CommonTable
 * in schedule-payment.tsx.
 */
export const ScheduledPaymentMobileCard: React.FC<ScheduledPaymentMobileCardProps> = ({ row }) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);

    return (
        <Box
            sx={{
                border: `1px solid ${expanded ? colors.primaryLight[100] : colors.greyPreset[30]}`,
                borderRadius: 1.5,
                backgroundColor: colors.fullWhite,
                overflow: 'hidden',
            }}
        >
            <Box sx={{ p: 1.5, cursor: 'pointer' }} onClick={() => setExpanded(prev => !prev)}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1.5}>
                    <Stack direction="row" gap={3} flexWrap="wrap">
                        <Stack spacing={0.25}>
                            <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.5 }}>
                                {(t('payments.upcomingPayments.contractId') || 'Contract').toUpperCase()}
                            </Typography>
                            <Stack direction="row" alignItems="center" gap={1}>
                                <Typography variant="body2" fontWeight={700}>
                                    {row.contractId}
                                </Typography>
                                {!expanded && row.paymentModeCompact}
                            </Stack>
                        </Stack>
                        <Stack spacing={0.25}>
                            <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.5 }}>
                                {(t('payments.schedulePayment.scheduledDate') || 'Scheduled Date').toUpperCase()}
                            </Typography>
                            <Typography variant="body2" fontWeight={700}>
                                {row.dueDate}
                            </Typography>
                        </Stack>
                    </Stack>
                    {expanded ? (
                        <Stack direction="row" alignItems="center" gap={1}>
                            {row.status}
                            <ChevronIcon expanded={expanded} />
                        </Stack>
                    ) : (
                        <Stack alignItems="flex-end" spacing={0.25}>
                            <Stack direction="row" alignItems="center" gap={0.5}>
                                <Typography variant="body2" fontWeight={700}>
                                    {row.amountCompact}
                                </Typography>
                                <ChevronIcon expanded={expanded} />
                            </Stack>
                            <Typography variant="caption" fontWeight={700} sx={{ color: row.statusColor }}>
                                {row.statusLabel}
                            </Typography>
                        </Stack>
                    )}
                </Stack>
            </Box>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <Box
                    sx={{
                        mx: 1.5,
                        mb: 1.5,
                        p: 1.5,
                        borderRadius: 1,
                        backgroundColor: colors.greyPreset[5],
                    }}
                >
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1.5}>
                        <Stack spacing={0.25}>
                            <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.5 }}>
                                {(t('payments.upcomingPayments.amount') || 'Amount').toUpperCase()}
                            </Typography>
                            <Typography variant="body2" fontWeight={700}>
                                {row.amount}
                            </Typography>
                        </Stack>
                        <Stack spacing={0.25} alignItems="flex-end">
                            <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.5 }}>
                                {(t('payments.schedulePayment.paymentMode') || 'Payment Mode').toUpperCase()}
                            </Typography>
                            {row.paymentMode}
                        </Stack>
                    </Stack>
                </Box>
                {row.hasCancelAction ? (
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-end" gap={1.5} px={1.5} pb={1.5}>
                        <Stack spacing={0.25} sx={{ maxWidth: '70%' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.5 }}>
                                {(t('payments.schedulePayment.comments') || 'Comments').toUpperCase()}
                            </Typography>
                            {row.comments || (
                                <Typography variant="body2" color="textSecondary">
                                    —
                                </Typography>
                            )}
                        </Stack>
                        <Box flexShrink={0}>{row.actions}</Box>
                    </Stack>
                ) : (
                    <Stack spacing={0.25} px={1.5} pb={1.5}>
                        <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.5 }}>
                            {(t('payments.schedulePayment.comments') || 'Comments').toUpperCase()}
                        </Typography>
                        {row.comments || (
                            <Typography variant="body2" color="textSecondary">
                                —
                            </Typography>
                        )}
                    </Stack>
                )}
            </Collapse>
        </Box>
    );
};
